<script setup lang="ts">
import { twMerge } from 'tailwind-merge';

import type { CarouselProps } from '#ui/types';
import lodashMergeWidth from 'lodash-es/mergeWith';
import lodashOmit from 'lodash-es/omit';

import { SellingMethod } from '~/apiClient';
import auctionBus from '~/assets/auction-bus.png';
import directSaleBus from '~/assets/direct-sale-bus.png';

export interface StoryblokValuePropositionCarouselProps {
    blok: StoryblokValuePropositionCarousel;
}

// PROPS
const { blok } = defineProps<StoryblokValuePropositionCarouselProps>();

// COMPOSABLES
const { tt, tn } = useTypedI18n();
const sellingMethod = useSlpSellingMethod();
const { soldVehicles } = useFleequidStats();

// DATA
const iconSrc = computed(() => (sellingMethod.value === SellingMethod.Auction ? auctionBus : directSaleBus));
const isOpen = ref<boolean>(false);
const items = computed(() => blok.items ?? []);
const carouselProps = computed<Omit<CarouselProps, 'items'>>(() =>
    lodashOmit(storyblokOmitEmptyValues<StoryblokValuePropositionCarousel>(blok), ['_uid', 'component', 'icon', 'title', 'description', 'items'])
);
const ui = computed(() => {
    const baseUi = {
        header: 'border-b-0 p-3 sm:py-3 sm:px-6',
        body: 'border-b-0 p-3 sm:py-3 sm:px-6',
        footer: 'p-3 pt-0 sm:py-3 sm:pt-0 sm:px-6',
    };
    const isClosedUi = { body: 'hidden', footer: 'hidden' };

    return lodashMergeWidth(baseUi, isOpen.value ? {} : isClosedUi, twMerge);
});

const carousel = useTemplateRef('carousel');
const prevDisabled = computed(() => selectedIndex.value === 0);
const nextDisabled = computed(() => selectedIndex.value + 1 === items.value.length);
const selectedIndex = ref<number>(0);

// HANDLERS
const resolveItemDescription = (description: string): string => description.replaceAll('{soldVehicles}', tn(soldVehicles.value));

const updateScrollState = (): void => {
    const emblaApi = carousel.value?.emblaApi;

    selectedIndex.value = emblaApi?.selectedScrollSnap() ?? 0;
};

const scrollPrev = (): void => {
    carousel.value?.emblaApi?.scrollPrev();
};

const scrollNext = (): void => {
    carousel.value?.emblaApi?.scrollNext();
};

// Set the initial enabled/disabled state once embla mounts; @select keeps it current afterwards.
watch(() => carousel.value?.emblaApi, updateScrollState);
</script>

<template>
    <BaseWidthContainer>
        <UCard v-if="items.length" v-editable="blok" :ui="ui">
            <template #header>
                <div class="flex items-center justify-between gap-3">
                    <div class="flex flex-1 items-center gap-3">
                        <img class="w-[60px]" draggable="false" loading="lazy" :src="iconSrc" alt="" aria-hidden="true" />

                        <div class="space-y-2">
                            <h4 class="text-primary text-xs font-black uppercase md:text-sm">{{ blok.title }}</h4>
                            <p class="text-muted m-0 text-xs md:text-sm">{{ blok.description }}</p>
                        </div>
                    </div>
                    <UButton
                        variant="link"
                        :ui="{ label: 'hidden md:block' }"
                        :label="isOpen ? tt('storyblok.valueProposition.hide') : tt('storyblok.valueProposition.readMore')"
                        :leading-icon="isOpen ? 'lucide:minus' : 'lucide:plus'"
                        @click="isOpen = !isOpen"
                    />
                </div>
            </template>

            <UCarousel
                ref="carousel"
                v-slot="{ item }"
                v-bind="carouselProps"
                :items="items"
                :ui="{ item: 'basis-full' }"
                @select="updateScrollState"
            >
                <div class="flex items-start gap-3 sm:gap-6">
                    <div class="flex flex-1 flex-col gap-3 sm:gap-6">
                        <p v-if="item.label" class="text-primary text-sm font-black uppercase">{{ item.label }}</p>
                        <h3 class="text-2xl font-bold">{{ item.title }}</h3>
                        <p class="text-toned">{{ resolveItemDescription(item.description) }}</p>

                        <UButton
                            v-if="item.to && storyblokResolveLink(item.to)"
                            variant="link"
                            class="pl-0"
                            trailing-icon="lucide:arrow-right"
                            :label="item.linkLabel ?? tt('storyblok.valueProposition.learnMore')"
                            :to="storyblokResolveLink(item.to)"
                        />
                    </div>

                    <NuxtImg
                        v-if="item.image?.filename"
                        format="webp"
                        class="hidden aspect-square w-3xs rounded-lg object-cover md:block lg:aspect-4/3 lg:w-sm xl:aspect-16/9 xl:w-xl"
                        draggable="false"
                        loading="lazy"
                        provider="storyblok"
                        :src="item.image.filename"
                        :alt="item.image.alt ?? ''"
                    />
                </div>
            </UCarousel>
            <template v-if="items.length > 1" #footer>
                <div class="flex items-center justify-center gap-3 sm:gap-6">
                    <UButton color="neutral" variant="outline" icon="lucide:chevron-left" :disabled="prevDisabled" @click="scrollPrev" />
                    <span class="text-muted text-sm tabular-nums">{{ selectedIndex + 1 }} / {{ items.length }}</span>
                    <UButton color="neutral" variant="outline" icon="lucide:chevron-right" :disabled="nextDisabled" @click="scrollNext" />
                </div>
            </template>
        </UCard>
    </BaseWidthContainer>
</template>
